import { API_URL } from "./config";

/**
 * Module-level store of the current sync URL for ZeroProvider.
 *
 * Single-region cutover (2026-05-18) made the desktop client's
 * cacheURL fully derivable from `home_region` alone:
 *
 *   - US-home  → `https://sync-us.ano.dev`         (US authority, direct)
 *   - EU-home  → `https://sync-replica-us.ano.dev` (EU-local replica
 *                                                   reading US PG via
 *                                                   WAL — ~5ms reads
 *                                                   instead of trans-
 *                                                   Atlantic ~150ms)
 *   - Unknown  → `https://sync-us.ano.dev`         (safe default; US
 *                                                   authority works
 *                                                   for everyone)
 *
 * Workspace switches do NOT change the URL — every workspace lives in
 * US PG. The URL is picked ONCE per session at sign-in (when
 * `home_region` resolves via the Worker `/route` call in AuthGate)
 * and cached in localStorage so subsequent app launches skip the
 * network round-trip.
 *
 * Why the simplification matters: pre-2026-05-19, this module had a
 * per-workspace dynamic URL resolution (`pickActiveSyncUrl`,
 * `prefetchRegionalUrls`, `cachedWorkspaceUrls` etc.) designed for the
 * planned-multi-region world where workspaces could live in different
 * PGs. After the single-region cutover, that machinery did no useful
 * work but DID produce spurious `<ZeroProvider>` re-keys — and the
 * `<AuthenticatedApp>` subtree (which lives inside `<ZeroProvider>`)
 * was being unmounted, losing React refs like `pendingWorkspaceRef`
 * and in-flight Zero subscriptions. That bug class shipped twice
 * (v0.1.54 channels-invisible, 2026-05-19 kicked-user dialog delay).
 *
 * When EU PG authority is re-introduced (future Phase 2 multi-region —
 * see `.claude/docs/multi-region-runbook.md` § "Desktop client region
 * machinery"), the per-workspace dynamic resolution comes back. Until
 * then, simple is correct.
 *
 * Non-prod builds: when the desktop bundle's `API_URL` isn't the prod
 * apex (`https://api.ano.dev`), we derive the sync URL from the API
 * URL by swapping `api` → `sync` (matches staging convention
 * `api-staging.ano.dev` ↔ `sync-staging.ano.dev`). `home_region` is
 * ignored in this branch because non-prod stacks are single-Zero.
 */

const PROD_API_URL = "https://api.ano.dev";
const HOME_REGION_CACHE_KEY = "ano:home-region:v1";

/**
 * Pick the sync URL for a user's home region. Pure function — exported
 * for testability. Production-shaped builds get the per-region URL;
 * non-prod builds (staging, local dev) derive from the build-time
 * API_URL.
 */
export function syncUrlForHomeRegion(homeRegion: "us" | "eu" | null): string {
  if (API_URL !== PROD_API_URL) {
    return deriveSyncFromApiUrl(API_URL);
  }
  if (homeRegion === "eu") return "https://sync-replica-us.ano.dev";
  return "https://sync-us.ano.dev";
}

/**
 * For non-prod builds: turn `https://api-staging.ano.dev` into
 * `https://sync-staging.ano.dev`. localhost / unmatched shapes pass
 * through unchanged (dev mode hits `selectCacheURL`'s explicit-env-var
 * branch anyway, so this branch is never the live cacheURL in dev).
 */
function deriveSyncFromApiUrl(apiUrl: string): string {
  const trimmed = apiUrl.replace(/\/+$/, "");
  return trimmed.replace(/^(https?:\/\/)api/, (_, scheme) => `${scheme}sync`);
}

function readCachedHomeRegion(): "us" | "eu" | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(HOME_REGION_CACHE_KEY);
    return v === "us" || v === "eu" ? v : null;
  } catch {
    return null;
  }
}

let cachedSyncUrl: string = syncUrlForHomeRegion(readCachedHomeRegion());
const listeners = new Set<() => void>();

/**
 * Inform the store that the user's home region has been resolved.
 *
 * Single call site today: `AuthGate`'s sign-in effect via
 * `resolveHomeRegion` — populates the in-memory store from the
 * localStorage cache or client-side timezone detection. Fast
 * cold-start path.
 *
 * A reactive reconciler (`HomeRegionWatcher`, v0.1.58) used to read
 * the Zero `users.home_region` row and call this whenever the
 * server value differed from the cache. That path was removed
 * because flipping `cacheURL` mid-session violates a Zero invariant:
 * cookie + clientGroupID are bound to the connected zero-cache
 * backend, but the JS Zero client uses the same IDB store regardless
 * of `cacheURL` — so the new replica sees stale identity from the
 * old one and rejects the connection, cascading into an infinite
 * ZeroProvider remount loop. Region routing is now static per
 * sign-in; travel = sign out + sign in.
 *
 * Persists the resolved region to `localStorage` so the next app
 * launch can skip detection and pick the right URL synchronously.
 * The cache is cleared on sign-out (see `logout.ts`) so a traveler
 * picks up the new region on the next sign-in. Persistence on
 * `null` is a no-op (don't overwrite a real value with "unknown").
 *
 * No-ops if the resolved URL would be the same as the current one —
 * avoids spurious `<ZeroProvider>` re-keys.
 */
export function setHomeRegionForZero(homeRegion: "us" | "eu" | null): void {
  if (
    typeof window !== "undefined" &&
    (homeRegion === "us" || homeRegion === "eu")
  ) {
    try {
      window.localStorage.setItem(HOME_REGION_CACHE_KEY, homeRegion);
    } catch {
      // Private browsing / quota — fall through. The in-memory
      // store still updates so the current session works; only the
      // next cold start is affected.
    }
  }

  const next = syncUrlForHomeRegion(homeRegion);
  if (next === cachedSyncUrl) return;
  cachedSyncUrl = next;
  for (const l of listeners) {
    try {
      l();
    } catch {
      // Subscriber threw — keep going so one bad subscriber can't
      // block the others.
    }
  }
}

/** `useSyncExternalStore` getSnapshot contract. Identity-stable. */
export function getActiveSyncUrl(): string {
  return cachedSyncUrl;
}

/** `useSyncExternalStore` subscribe contract. */
export function subscribeActiveSyncUrl(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Test helper — resets the module's internal state. Production code
 * should never call this; the singleton store is intentional.
 */
export function __resetActiveRegionForTests(): void {
  cachedSyncUrl = syncUrlForHomeRegion(readCachedHomeRegion());
  listeners.clear();
}
